import { featureLabel, serviceLabel } from 'lib';
import { useTranslation } from 'react-i18next';
import { badge, fmtCount, fmtDuration, fmtPercent } from './helpers';

const card = 'bg-white border border-[#E5E7EB] rounded-xl p-4 mb-4';
const statLabel = 'text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide';

const Stat = ({ label, value, tone }) => (
  <div className="flex flex-col gap-[2px] min-w-[110px]">
    <span className={statLabel}>{label}</span>
    <span className={`text-[18px] font-bold text-[#1E293B] leading-tight ${tone || ''}`}>
      {value}
    </span>
  </div>
);

const MetricsBar = ({ metrics, loading, groupBy, onGroupByChange }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className={`${card} text-center text-[13px] text-[#97A3B5]`}>
        {t('admin.activityLog.loading', { defaultValue: 'Loading…' })}
      </div>
    );
  }
  if (!metrics) return null;

  /*
    A period with no activity is a genuine zero and says so. Rendering nothing here would leave an
    admin unable to tell "nothing happened" from "nothing was recorded".
  */
  if (!metrics.hasData) {
    return (
      <div className={`${card} text-center text-[13px] text-[#64748B]`}>
        {t('admin.activityLog.metrics.zero', {
          defaultValue:
            'No activity recorded in this period. This is a real zero, not missing data.'
        })}
      </div>
    );
  }

  const totalCalls = (metrics.services || []).reduce((sum, s) => sum + (s.calls || 0), 0);
  const totalFailures = (metrics.services || []).reduce((sum, s) => sum + (s.failures || 0), 0);
  const totalRuns = (metrics.jobs || []).reduce((sum, j) => sum + (j.runs || 0), 0);
  const failedRuns = (metrics.jobs || []).reduce((sum, j) => sum + (j.failed || 0), 0);

  return (
    <div className={card}>
      <div className="flex flex-wrap gap-6 mb-4">
        <Stat
          label={t('admin.activityLog.metrics.calls', { defaultValue: 'Provider calls' })}
          value={fmtCount(totalCalls)}
        />
        <Stat
          label={t('admin.activityLog.metrics.failures', { defaultValue: 'Failed calls' })}
          value={fmtCount(totalFailures)}
          tone={totalFailures > 0 ? 'text-[#E11D48]' : ''}
        />
        <Stat
          label={t('admin.activityLog.metrics.failureRate', { defaultValue: 'Failure rate' })}
          value={fmtPercent(totalCalls > 0 ? totalFailures / totalCalls : 0)}
        />
        <Stat
          label={t('admin.activityLog.metrics.runs', { defaultValue: 'Job runs' })}
          value={fmtCount(totalRuns)}
        />
        <Stat
          label={t('admin.activityLog.metrics.failedRuns', { defaultValue: 'Failed runs' })}
          value={fmtCount(failedRuns)}
          tone={failedRuns > 0 ? 'text-[#E11D48]' : ''}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse mb-3">
          <thead className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
            <tr>
              {[
                t('admin.activityLog.metrics.service', { defaultValue: 'Service' }),
                t('admin.activityLog.metrics.calls', { defaultValue: 'Calls' }),
                t('admin.activityLog.metrics.failures', { defaultValue: 'Failures' }),
                t('admin.activityLog.metrics.rate', { defaultValue: 'Rate' }),
                t('admin.activityLog.metrics.typical', { defaultValue: 'Typical' }),
                t('admin.activityLog.metrics.worst', { defaultValue: 'Worst' })
              ].map((h) => (
                <th
                  key={h}
                  className="text-left text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide px-3 py-2"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(metrics.services || []).map((s) => (
              <tr key={s.service} className="border-b border-[#F1F5F9]">
                <td className="px-3 py-2 text-[13px] text-[#353E4A]">
                  <div className="flex items-center gap-2">
                    {serviceLabel(s.service)}
                    {s.source === 'entries' && (
                      <span className={badge('muted')}>
                        {t('admin.activityLog.metrics.fromEntries', {
                          defaultValue: 'from entries — limited to the retention window'
                        })}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-[13px]">{fmtCount(s.calls)}</td>
                <td className="px-3 py-2 text-[13px]">{fmtCount(s.failures)}</td>
                <td className="px-3 py-2 text-[13px]">{fmtPercent(s.failureRate)}</td>
                <td className="px-3 py-2 text-[13px]">{fmtDuration(s.durationTypicalMs)}</td>
                <td className="px-3 py-2 text-[13px]">{fmtDuration(s.durationWorstMs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className={statLabel}>
          {t('admin.activityLog.metrics.breakdown', { defaultValue: 'Break down by' })}
        </span>
        {['none', 'feature', 'environment'].map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => onGroupByChange(key === 'none' ? '' : key)}
            className={`h-[28px] px-3 rounded-lg border text-[12px] font-medium ${
              (groupBy || 'none') === key
                ? 'bg-[#334155] text-white border-[#334155]'
                : 'bg-white text-[#475569] border-[#D7DEE8] hover:bg-[#F1F5F9]'
            }`}
          >
            {key}
          </button>
        ))}
      </div>

      {metrics.breakdown?.length ? (
        <div className="flex flex-wrap gap-2 mt-3">
          {metrics.breakdown.map((row) => (
            <span
              key={row.key}
              className="px-3 py-1 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-[12px] text-[#353E4A]"
            >
              {groupBy === 'feature' ? featureLabel(row.key) : row.key}:{' '}
              <span className="font-semibold">{fmtCount(row.calls)}</span>
              {row.failures ? (
                <span className="text-[#E11D48]"> ({fmtCount(row.failures)} failed)</span>
              ) : null}
            </span>
          ))}
          {/* The parts must sum to the total; showing both lets an admin verify it at a glance. */}
          <span className="px-3 py-1 text-[12px] text-[#64748B]">
            {t('admin.activityLog.metrics.sums', { defaultValue: 'sums to' })}{' '}
            {fmtCount(metrics.breakdown.reduce((sum, r) => sum + (r.calls || 0), 0))}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default MetricsBar;
